import { Grid, IconButton, InputAdornment } from '@mui/material';
import { memo, useCallback } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import { FormikErrors, FormikTouched } from 'formik';
import { MilanPlaceVM } from 'front-office/view-models/authorization-vote';
import { Search } from '@mui/icons-material';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __ELECTORAL_API_URL__ } from 'shell/constants/environment';
import { HttpResponse } from 'shell/models/http-response';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { MilanPlace as MilanPlaceModel } from 'shell/models/person/electoral-card';

interface MilanPlaceComponentProps {
  values?: MilanPlaceVM | null;
  onChange: (key: keyof MilanPlaceVM, value: MilanPlaceVM[keyof MilanPlaceVM]) => void;
  onChangeValues: (values: MilanPlaceVM) => void;
  disabled?: boolean;
  touched?: FormikTouched<MilanPlaceVM>;
  errors?: FormikErrors<MilanPlaceVM>;
}

const MilanPlaceComponent = ({
  values,
  onChange,
  onChangeValues,
  disabled,
  touched,
  errors,
}: MilanPlaceComponentProps): React.ReactElement => {
  const getSection = useHttpClient<HttpResponse<MilanPlaceModel>>(
    `${__ELECTORAL_API_URL__}/persona/autorizzazione-al-voto/dettaglio-sezione`
  );

  const handleSearch = useCallback(() => {
    if (!values?.section) {
      return;
    }
    (
      getSection({
        url: `/${values?.section}`,
      }) as ResponsePromise<HttpResponse<MilanPlaceModel>>
    ).then((response) => {
      onChangeValues({
        description: response?.data.payload.descrizione,
        zone: response?.data.payload.zona,
        collegioCamera: response?.data.payload.collegioCamera?.toString() || '',
        collegioProvinciale: response?.data.payload.collegioProvinciale?.toString() || '',
        collegioSenato: response?.data.payload.collegioSenato?.toString() || '',
      });
    });
  }, [onChangeValues, getSection, values?.section]);

  return (
    <>
      <Grid item xs={12} md={4}>
        <PmiTextField
          aria-label="Sezione Casa di cura *"
          label="Sezione Casa di cura *"
          id="section"
          name="section"
          type="text"
          onChange={(event) => {
            onChange('section', event.target.value);
          }}
          fullWidth
          variant="outlined"
          value={values?.section || ''}
          disabled={disabled}
          error={touched?.section && Boolean(errors?.section)}
          helperText={errors?.section}
          InputProps={{
            inputProps: { maxLength: 4 },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => {
                    handleSearch();
                  }}
                  disabled={disabled}
                >
                  <Search color="secondary" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <PmiTextField
          aria-label="Descrizione Casa di cura"
          label="Descrizione Casa di cura"
          id="description"
          name="description"
          type="text"
          onChange={(event) => {
            onChange('description', event.target.value);
          }}
          fullWidth
          variant="outlined"
          value={values?.description || ''}
          disabled
          error={touched?.description && Boolean(errors?.description)}
          helperText={errors?.description}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <DatePicker
          label="Data richiesta *"
          aria-label="Data richiesta *"
          value={values?.requestDate || null}
          disabled={disabled}
          onChange={(date) => {
            onChange('requestDate', date);
          }}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="requestDate"
              fullWidth
              error={touched?.requestDate && Boolean(errors?.requestDate)}
              helperText={errors?.requestDate}
            />
          )}
        />
      </Grid>
    </>
  );
};

MilanPlaceComponent.displayName = 'Authorization data component';

const MilanPlace = memo(MilanPlaceComponent);

export default MilanPlace;
